/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { useEffect, useRef } from 'react'
import './styles/globals.css'
import { Router } from './router'
import { Toast } from './components/common/Toast'

import { useUIStore } from './state/uiStore'
import { useAuthStore } from './state/authStore'

import { UpdateManager } from './core/pwa/UpdateManager'
import { APP_FULL_NAME } from './config/env'
import { initSyncService, stopSyncService } from './services/syncService'

function App() {
  const { setTheme } = useUIStore()
  const { initialize: initializeAuth } = useAuthStore()
  const user = useAuthStore((s) => s.user)

  const prevUserIdRef = useRef<string | null>(null)
  const currentUserId = user ? String(user.id) : null

  // Initialiser le thème au démarrage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
    if (savedTheme) {
      setTheme(savedTheme)
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark')
    }
  }, [setTheme])

  // Initialiser l'authentification au démarrage
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // Démarrer / arrêter la sync selon l'état d'authentification
  useEffect(() => {
    if (currentUserId && currentUserId !== prevUserIdRef.current) {
      // Nouvel utilisateur connecté (login ou hydratation)
      initSyncService(currentUserId)
    } else if (!currentUserId && prevUserIdRef.current) {
      // Déconnexion
      stopSyncService()
    }

    prevUserIdRef.current = currentUserId
  }, [currentUserId])

  // Mettre à jour le titre du document avec le nom d'application dynamique
  useEffect(() => {
    document.title = APP_FULL_NAME
  }, [])

  return (
    <>
      <Router />
      <Toast />

      <UpdateManager checkInterval={60 * 60 * 1000} autoUpdate={false} />
    </>
  )
}

export default App
